import { useCallback } from 'react';

import type {
  WRegistryCollectionItemView,
  WProductView,
  WSkuPreview,
  WProductLookView,
} from '@zola/svc-web-api-ts-client';
import * as toastsActions from '@zola/zola-ui/src/actions/toastsV2Actions';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { UserContext } from '@zola-helpers/client/dist/types/@types';

import type { AddToRegistryRequest } from '@/actions/RegistryCollectionActions';
import { useManageRegistryCollectionContext } from '@/contexts/registry/ManageRegistryCollectionContext/ManageRegistryCollectionContext';
import { useRegistryShoppingContext } from '@/contexts/shop/RegistryContext/RegistryShoppingContext';
import { useAppDispatch, useAppSelector } from '@/store';
import { trackRegistryItemAdded } from '@/util/trackingHelper';

import {
  getKitRegistryItemAddedEventData,
  KitRegistryItemAddedEventParams,
  getRegistryItemAddedEventData,
} from './pdpHelpers';

type AddToRegistryCallbackParams = {
  productData: WProductView | null;
  activeSku: WSkuPreview | null;
  quantity: number;
  addToRegistry: (
    data: AddToRegistryRequest,
    productShippingZone?: string[] | undefined,
    productShippingExcludedStates?: string[]
  ) => Promise<any>;
  personalizationData?: null | {
    uuid: string | undefined;
    sku_id: string;
  };

  createRegistryForExistingUser: () => Promise<void>;
  redirectToOnboarding: () => void;
  hasRegistry: boolean;
  canCreateRegistry: boolean;
};

const onAddToRegistry = (
  params: AddToRegistryCallbackParams,
  dispatchCallback?: (collectionItemView: WRegistryCollectionItemView | undefined) => void
) => {
  const {
    productData,
    activeSku,
    quantity,
    addToRegistry,
    personalizationData = null,
    createRegistryForExistingUser,
    redirectToOnboarding,
    hasRegistry,
    canCreateRegistry,
  } = params;

  return (quantityOverride?: number, skuOverride?: string) => {
    if (!activeSku) {
      return Promise.resolve(null);
    }

    const skuId = activeSku.id as string;
    const request: AddToRegistryRequest = {
      sku_id: skuId,
      enable_group_gifting: false,
      quantity,
    };

    const doAddToRegistry = () => {
      // carousel values are only used in the main PDP to determine whether its the main product or a carousel product being added
      // they are not relevant to the kit product detail rows
      if (Number(quantityOverride)) {
        request.quantity = quantityOverride;
        request.sku_id = skuOverride as string;
        return addToRegistry(
          request,
          productData?.shipping_zones,
          productData?.shipping_excluded_states
        );
      }
      return addToRegistry(
        request,
        productData?.shipping_zones,
        productData?.shipping_excluded_states
      )
        .then(dispatchCallback)
        .then(() => null);
    };

    if (personalizationData) {
      request.user_selection_uuid = personalizationData.uuid;
      request.sku_id = personalizationData.sku_id;
    }
    if (hasRegistry) {
      return doAddToRegistry();
    }

    if (canCreateRegistry) {
      return createRegistryForExistingUser().then(() => doAddToRegistry());
    }

    return Promise.resolve()
      .then(() => redirectToOnboarding())
      .then(() => null);
  };
};

type AddToRegistryFromPDP = (quantity?: number, skuId?: string) => null | Promise<any>;

export const useAddToRegistryFromPDP = ({
  productData,
  activeSku,
  userContext,
  quantity,
}: {
  activeSku: WSkuPreview | null;
  productData: WProductView | null;
  quantity: number;
  userContext: UserContext | null;
}): AddToRegistryFromPDP => {
  const { createRegistryForExistingUser, redirectToOnboarding, hasRegistry, canCreateRegistry } =
    useRegistryShoppingContext();

  const { addToRegistry } = useManageRegistryCollectionContext();

  const dispatch = useAppDispatch();

  const addToRegistryParams: AddToRegistryCallbackParams = {
    productData,
    activeSku,
    quantity,
    addToRegistry,
    createRegistryForExistingUser,
    redirectToOnboarding,
    hasRegistry,
    canCreateRegistry,
  };

  const savedPersonalization = useAppSelector(
    (state) => state.personalization.savedPersonalization
  );

  if (savedPersonalization && savedPersonalization.sku_id) {
    const personalizationData = {
      uuid: savedPersonalization.uuid,
      sku_id: savedPersonalization.sku_id,
    };
    addToRegistryParams.personalizationData = personalizationData;
  }

  const addToRegistryCallback = useCallback(
    (collectionItemView: WRegistryCollectionItemView | undefined) => {
      if (collectionItemView) {
        // TODO Baby: Handle add to registry callback in a baby context
        if (
          userContext &&
          userContext.user_role_account_weddings &&
          userContext.user_role_account_weddings[0]
        ) {
          const { slug } = userContext.user_role_account_weddings[0];
          const headline = (
            <div className="addedToRegistryToast">
              <span>{quantity} Added to Your Registry</span>
              <LinkV2 className="registryCta" href={`/registry/${slug}`}>
                View Registry
              </LinkV2>
            </div>
          );
          dispatch(toastsActions.positive({ headline }));
        }
        if (productData && activeSku) {
          const eventData = getRegistryItemAddedEventData({
            productData,
            activeSku,
            quantity,
          });
          trackRegistryItemAdded(eventData);
        }
      }
      return null;
    },
    [userContext, dispatch, productData, activeSku, quantity]
  );

  return onAddToRegistry(addToRegistryParams, addToRegistryCallback);
};

export const useAddToRegistryFromPDPKit = ({
  activeProductLook,
  activeSku,
  productData,
  quantity,
  userContext,
  ...trackingParams
}: {
  activeSku: WSkuPreview | null;
  activeProductLook: WProductLookView | null;
  productData: WProductView | null;
  quantity: number;
  userContext: UserContext | null;
} & Pick<
  KitRegistryItemAddedEventParams,
  'location' | 'section' | 'extraData'
>): AddToRegistryFromPDP => {
  const { createRegistryForExistingUser, redirectToOnboarding, hasRegistry, canCreateRegistry } =
    useRegistryShoppingContext();

  const { addToRegistry } = useManageRegistryCollectionContext();

  const dispatch = useAppDispatch();

  const addToRegistryParams: AddToRegistryCallbackParams = {
    productData,
    activeSku,
    quantity,
    addToRegistry,
    createRegistryForExistingUser,
    redirectToOnboarding,
    hasRegistry,
    canCreateRegistry,
  };

  const addToRegistryCallback = useCallback(
    (collectionItemView: WRegistryCollectionItemView | undefined) => {
      if (collectionItemView) {
        if (
          userContext &&
          userContext.user_role_account_weddings &&
          userContext.user_role_account_weddings[0]
        ) {
          const { slug } = userContext.user_role_account_weddings[0];
          const headline = (
            <div className="addedToRegistryToast">
              <span>{quantity} Added to Your Registry</span>
              <LinkV2 className="registryCta" href={`/registry/${slug}`}>
                View Registry
              </LinkV2>
            </div>
          );
          dispatch(toastsActions.positive({ headline }));
        }
        const eventData = getKitRegistryItemAddedEventData({
          productData,
          activeProductLook,
          activeSku,
          quantity,
          ...trackingParams,
        });
        trackRegistryItemAdded(eventData);
      }
    },
    [activeProductLook, activeSku, dispatch, productData, quantity, trackingParams, userContext]
  );

  return onAddToRegistry(addToRegistryParams, addToRegistryCallback);
};
